import * as React from 'react';
import { InfoWindow, Marker } from '@react-google-maps/api';
import { Instance } from 'mobx-state-tree';
import { NavLink } from 'react-router-dom';
import { Page, TLCode } from '../../common/enums';
import TranslatedMessage from './translation/TranslatedMessage';
import { Button } from '@blueprintjs/core';
import styled from '@emotion/styled';
import { Pharmacy } from '../../store/models/Pharmacy';
import { IconNames } from '@blueprintjs/icons';
import SimplePharmacyCalendar from './calendar/SimplePharmacyCalendar';
import moment from 'moment';
import { Group } from '../../store/models/Group';
import { inject } from 'mobx-react';
import { PharmacyStatusStore, AuthenticationStore, GroupStore } from '../../store';
import withTranslate, { WithTranslateProps } from './WithTranslate';
import { PHARMACY_STATUS_TC } from '../../common/constants/pharmacyStatusConstants';

export interface PharmacyMarkerProps extends WithTranslateProps {
  marker: Instance<typeof Pharmacy>;
  icon: any;
  zIndex?: number;
  onSelectionClick?: (pharmacy: Instance<typeof Pharmacy>) => void;
  // onAssignClick?: (dutyAssignmentId: number, pharmacyId: number, type: string) => void;
  onUnassignClick?: (dutyAssignmentId: number, pharmacyId: number) => void;
  isFree?: (pharmacyId: number) => boolean;
  isDayOnly?: (pharmacyId: number) => boolean;
  isDayAndNight?: (pharmacyId: number) => boolean;
  onAddDayOnlyClick?: (date: string, pharmacyId: number) => void;
  onAddDayNightClick?: (date: string, pharmacyId: number) => void;
  onChangeDayOnlyToDayNightClick?: (dutyAssignmentId: number, pharmacyId: number) => void;
  onChangeDayNightToDayOnlyClick?: (dutyAssignmentId: number, pharmacyId: number) => void;
  onMarkForGroupChangeClick?: (pharmacyId: number) => void;
  onCalendarChanged?: (pharmacyId: number) => void;
  onTogglePairingAllowedClick?: (pharmacyNeighborId: number) => void;
  onToggleExchangeAllowedClick?: (pharmacyNeighborId: number) => void;
  onTerminateProtectionClick?: (protectionId: number) => void;
  pharmacyNeighborId?: number;
  onAddCircle?: (lat: number, long: number) => void;
  id?: number;
  date?: string;
  groupEditMode?: boolean;
  protections?: any;
  disableRightClick?: boolean;
  group?: Instance<typeof Group>;
  simpleCalendarStartYear?: number;
  PharmacyStatusStore?: Instance<typeof PharmacyStatusStore>;
  AuthenticationStore?: Instance<typeof AuthenticationStore>;
  dutiesassignmentsList: any;
  showDutiesCountInDescription?: boolean | undefined;
  addOrRemoveMaskedPharmaciesProctectionCircles?: (apbNumber: number) => void;
  isMaskedPharmaciesProctectionCircles?: boolean;
}

const DivLine = styled.div`
  display: flex;
  flex-direction: row;
  justify-content: space-between;
`;

export interface PharmacyMarkerState {
  isOpen: boolean;
  isPinned: boolean;
  showContextMenu: boolean;
  groupEditMode: boolean;
  dutiesCountForPharmacy: number | undefined;
  dayDutiesCountForPharmacy: number | undefined;
  nightDutiesCountForPharmacy: number | undefined;
}

@inject('PharmacyStatusStore', 'AuthenticationStore')
class PharmacyMarker extends React.Component<PharmacyMarkerProps, PharmacyMarkerState> {
  constructor(props) {
    super(props);
    this.state = {
      isOpen: false,
      isPinned: false,
      showContextMenu: false,
      groupEditMode: this.props.groupEditMode ? this.props.groupEditMode! : false,
      dutiesCountForPharmacy: undefined,
      dayDutiesCountForPharmacy: undefined,
      nightDutiesCountForPharmacy: undefined,
    };
  }

  handleToggle = () => {
    this.setState({
      isOpen: false,
      isPinned: false,
    });
    // if (this.props.onCalendarChanged) {
    //   this.props.onCalendarChanged(this.props.marker.pharmacyId);
    // }
  };

  handleTogglePinned = () => {
    this.setState({
      isPinned: !this.state.isPinned,
      isOpen: !this.state.isPinned,
    });
  };

  handleToggleContextMenu = () => {
    this.setState({ showContextMenu: !this.state.showContextMenu });
  };

  handleCloseContextMenu = () => {
    if (this.props.onCalendarChanged) {
      this.props.onCalendarChanged(this.props.marker.pharmacyId);
    }
  };

  isPharmacyTemporarilyClosed = (statusId) => {
    const pharmacyStatus = this.props.PharmacyStatusStore!.dataList.find((ps) => ps.pharmacyStatusId === statusId);

    return pharmacyStatus != undefined && pharmacyStatus.code === PHARMACY_STATUS_TC;
  };

  async getDutiesCount(pharmacyId: number) {
    var duties = [] as any[];
    if (this.props.dutiesassignmentsList)
      duties = this.props.dutiesassignmentsList.filter((x) => x.pharmacyId == pharmacyId);

    var dayDutyDates = duties.filter((x) => x.duty.dutyType.type === 'DAY').map((x) => x.duty.dutyDate.date);
    var nightDutyDates = duties.filter((x) => x.duty.dutyType.type === 'NIGHT').map((x) => x.duty.dutyDate.date);
    var dutiesDates = duties.map((x) => x.duty.dutyDate.date);

    // Dates uniques pour chaque type
    const uniqueDayDates = [...new Set(dayDutyDates)];
    const uniqueNightDates = [...new Set(nightDutyDates)];

    // Dates exclusivement DAY (pas de NIGHT le même jour)
    const exclusiveDayDates = uniqueDayDates.filter((date) => !uniqueNightDates.includes(date));

    // Dates avec NIGHT (soit exclusivement NIGHT, soit DAY+NIGHT)
    const nightOrDayNightDates = uniqueNightDates;

    // Comptages finaux
    var dutiesCount = [...new Set(dutiesDates)].length;
    var dayDutiesCount = exclusiveDayDates.length;
    var nightDutiesCount = nightOrDayNightDates.length;

    this.setState({
      dutiesCountForPharmacy: dutiesCount,
      dayDutiesCountForPharmacy: dayDutiesCount,
      nightDutiesCountForPharmacy: nightDutiesCount,
    });
  }

  render() {
    const { id, translate, AuthenticationStore } = this.props;

    return (
      <>
        {id && (
          <Marker
            key={this.props.marker.pharmacyId}
            position={{ lat: Number(this.props.marker.latitude), lng: Number(this.props.marker.longitude) }}
            icon={this.props.icon}
            onClick={() => this.handleTogglePinned()}
            onRightClick={() => {
              if (!this.props.disableRightClick) this.handleToggleContextMenu();
            }}
            onMouseOver={() => {
              this.getDutiesCount(this.props.marker.pharmacyId);
              this.setState({
                isOpen: true,
              });
            }}
            onMouseOut={() =>
              this.setState({
                isOpen: false,
              })
            }
            zIndex={this.props.zIndex}
          >
            {this.state.showContextMenu && (
              <InfoWindow options={{ maxWidth: 1200, minWidth: 1200 }} onCloseClick={this.handleCloseContextMenu}>
                <SimplePharmacyCalendar
                  id={'topCal'}
                  pharmacyId={this.props.marker.pharmacyId}
                  hideLegend={true}
                  synchronizedScrolling={false}
                  fly={this.props.simpleCalendarStartYear}
                  // onCalendarMove={this.onCalendarMove}
                  // onTypeChange={this.onTypeChange}
                  // selectedType={this.state.selectedType}
                  separateToolbox={true}
                  inMultiCalendar={false}
                />
              </InfoWindow>
            )}
            {(this.state.isOpen || this.state.isPinned) && (
              <InfoWindow onCloseClick={this.handleToggle} options={{ disableAutoPan: true }}>
                <div>
                  <DivLine>
                    <NavLink to={Page.PharmacyPage + '/' + this.props.marker.pharmacyId}>
                      <h3>
                        {this.isPharmacyTemporarilyClosed(this.props.marker.statusId) &&
                          '(' + translate(TLCode.PHARMACY_STATUS_TEMP_CLOSED) + ') '}{' '}
                        {this.props.marker.apbNumber} - {this.props.marker.name} - {this.props.marker.phone}
                      </h3>
                    </NavLink>
                    &nbsp;&nbsp;&nbsp;&nbsp;&nbsp;&nbsp;
                  </DivLine>
                  <DivLine>
                    {this.props.marker.address},&nbsp;{this.props.marker.zipCode}&nbsp;{this.props.marker.city}
                    <div style={{ textAlign: 'right' }}>
                      niv:{this.props.marker.level}&nbsp;&nbsp;Grp:
                      {this.props.group !== undefined ? this.props.group.name : ''}
                    </div>
                  </DivLine>
                  {/* {(AuthenticationStore!.isSuperAdmin || AuthenticationStore!.isAdmin) && this.props.showDutiesCountInDescription && */}
                  
                  {!AuthenticationStore!.isPharmacy && this.props.showDutiesCountInDescription &&
                  <DivLine style={{ marginTop: '10px' }}>
                      {translate(TLCode.DUTIES_DAY_AND_NIGHT_COUNTER,
                        {
                          dutiesCount: this.state.dutiesCountForPharmacy!.toString() || '',
                          dutiesDayCount: this.state.dayDutiesCountForPharmacy!.toString() || '',
                          dutiesNightCount: this.state.nightDutiesCountForPharmacy!.toString() || ''
                        })}
                    </DivLine>
                  }
                  {this.props.protections &&
                    this.props.protections.map((protection) => {
                      return (
                        <>
                          <DivLine style={{ marginTop: '10px' }}>
                            {moment(protection.endDate).format('DD/MM/YYYY') + '  ' + protection.range + 'm'}
                            &nbsp;
                            <Button
                              onClick={() => {
                                this.props.onTerminateProtectionClick!(protection.protectionId);
                              }}
                              intent="danger"
                            >
                              <TranslatedMessage id={TLCode.TRANSFER_TerminateProtection} />
                            </Button>
                          </DivLine>
                          <br />
                        </>
                      );
                    })}

                  <div style={{ marginTop: '15px' }}>
                    {this.props.onSelectionClick && (
                      <Button
                        onClick={() => {
                          this.props.onSelectionClick!(this.props.marker);
                          this.handleTogglePinned();
                        }}
                        intent="primary"
                      >
                        {/* {this.state.selectButtonLabel} */}
                        <TranslatedMessage id={TLCode.GENERAL_Select} />
                      </Button>
                    )}
                    {this.props.onAddDayOnlyClick && this.props.isFree!(this.props.marker.pharmacyId) && (
                      <Button
                        style={{ marginLeft: '20px' }}
                        onClick={() => {
                          this.props.onAddDayOnlyClick!(this.props.date!, this.props.marker.pharmacyId);
                          this.handleCloseContextMenu();
                        }}
                        intent="danger"
                      >
                        + J
                      </Button>
                    )}
                    {this.props.onAddDayNightClick && this.props.isFree!(this.props.marker.pharmacyId) && (
                      <Button
                        style={{ marginLeft: '20px' }}
                        onClick={() => {
                          this.props.onAddDayNightClick!(this.props.date!, this.props.marker.pharmacyId);
                          this.handleCloseContextMenu();
                        }}
                        intent="danger"
                      >
                        + J+N
                      </Button>
                    )}
                    {this.props.onChangeDayOnlyToDayNightClick && this.props.isDayOnly!(this.props.marker.pharmacyId) && (
                      <Button
                        style={{ marginLeft: '20px' }}
                        onClick={() => {
                          this.props.onChangeDayOnlyToDayNightClick!(id!, this.props.marker.pharmacyId);
                          this.handleCloseContextMenu();
                        }}
                        intent="danger"
                      >
                        -&gt;&nbsp;J+N
                      </Button>
                    )}
                    {this.props.onUnassignClick && (
                      <Button
                        style={{ marginLeft: '20px' }}
                        onClick={() => {
                          this.props.onUnassignClick!(id!, this.props.marker.pharmacyId);
                        }}
                        intent="danger"
                        icon={IconNames.DELETE}
                      />
                    )}
                    {this.props.onChangeDayNightToDayOnlyClick &&
                      this.props.isDayAndNight!(this.props.marker.pharmacyId) && (
                        <Button
                          style={{ marginLeft: '20px' }}
                          onClick={() => {
                            this.props.onChangeDayNightToDayOnlyClick!(id!, this.props.marker.pharmacyId);
                            this.handleCloseContextMenu();
                          }}
                          intent="danger"
                        >
                          -&gt;&nbsp;J
                        </Button>
                      )}
                    {this.props.groupEditMode && this.props.onMarkForGroupChangeClick && (
                      <Button
                        style={{ marginLeft: '20px' }}
                        onClick={() => {
                          this.props.onMarkForGroupChangeClick!(this.props.marker.pharmacyId);
                          this.handleCloseContextMenu();
                        }}
                        intent="primary"
                      >
                        <TranslatedMessage id={TLCode.GENERAL_Mark} />
                      </Button>
                    )}
                    {this.props.onTogglePairingAllowedClick && this.props.pharmacyNeighborId && (
                      <Button
                        style={{ marginLeft: '20px' }}
                        onClick={() => {
                          this.props.onTogglePairingAllowedClick!(this.props.pharmacyNeighborId!);
                        }}
                        intent="danger"
                      >
                        CO
                      </Button>
                    )}
                    {this.props.onToggleExchangeAllowedClick && this.props.pharmacyNeighborId && (
                      <Button
                        style={{ marginLeft: '20px' }}
                        onClick={() => {
                          this.props.onToggleExchangeAllowedClick!(this.props.pharmacyNeighborId!);
                        }}
                        intent="danger"
                      >
                        EX
                      </Button>
                    )}
                  </div>
                  {this.props.onAddCircle && (
                    <DivLine
                      style={{
                        alignItems: 'center',
                        justifyContent: 'center',
                        marginTop: '10px',
                      }}
                    >
                      <Button
                        style={{ fontSize: 12, marginLeft: '10px', marginRight: '10px' }}
                        onClick={() => {
                          if (this.props.onAddCircle) {
                            this.props.onAddCircle(
                              Number(this.props.marker.latitude),
                              Number(this.props.marker.longitude),
                            );
                            this.handleTogglePinned();
                          }
                        }}
                      >
                        <TranslatedMessage id={TLCode.TRANSFER_AddCircleDialogTitle} />
                      </Button>
                      {this.props.protections && this.props.protections.length > 0 && (
                        <Button
                          style={{ fontSize: 12, marginLeft: '10px', marginRight: '10px' }}
                          onClick={() => {
                            this.props.addOrRemoveMaskedPharmaciesProctectionCircles!(this.props.marker.apbNumber);
                          }}
                        >
                          <TranslatedMessage id={this.props.isMaskedPharmaciesProctectionCircles ? TLCode.TRANSFER_ShowCircleDialogTitle : TLCode.TRANSFER_RemoveCircleDialogTitle} />
                        </Button>
                      )}
                    </DivLine>
                  )}
                </div>
              </InfoWindow>
            )}
          </Marker>
        )}
      </>
    );
  }
}

export default withTranslate(PharmacyMarker);
